import { isEqual } from 'underscore';
import { computed, ref, watch, type Ref } from 'vue';
import { tracker } from '@/entities/analytics';
import type { DestinationPrices } from '@/entities/searchCriteria';
import { getCountryResorts } from '@/shared/api/getCountryResorts';
import { getOptions } from '@/shared/api/getOptions';
import type { Country, Destination, DestinationKind, Hotel, Resort } from '@/shared/model';
import {
  emptySelection,
  getSelectedCountry,
  getSelectedHotels,
  getSelectedResorts,
  isCountry,
  isHotels,
  isNone,
  isResorts,
  selectCountry as selectCountryTransition,
  toggleResort as toggleResortTransition,
  selectAllResorts as selectAllResortsTransition,
  selectHotel as selectHotelTransition,
  type Selection,
} from './selection';
import type { OptionGroup } from './types';
import { DESTINATION_TEXTS } from '../config/texts';
import { buildLabel } from '../lib/buildLabel';
import { computeInputHint } from '../lib/computeInputHint';
import { countryResortCatalog, resolveCountryFromList } from '../lib/countryLookup';
import { destinationEntity } from '../lib/destinationEntity';
import { groupOptions } from '../lib/groupOptions';
import { sortOptions } from '../lib/sortOptions';

export interface UseDestinationOptions {
  model: Ref<Selection>;
  initialCountries?: Country[];
  prices?: () => DestinationPrices | undefined;
  searchUrl?: string;
  resortsUrl?: string;
}

export function useDestination(options: UseDestinationOptions) {
  const model = options.model;
  let lastEmitted: Selection | undefined = undefined as unknown as Selection;
  let lastNonEmptySelection: Selection = model.value;
  const selectionRef = ref<Selection>(model.value);
  const setSelection = (next: Selection): void => {
    lastEmitted = next;
    selectionRef.value = next;
    model.value = next;
    if (!isNone(next)) {
      lastNonEmptySelection = next;
    }
  };

  const readPrices = (): DestinationPrices => options.prices?.() ?? {};

  const withCountryPrice = (country: Country): Country => {
    const external = readPrices().country?.[country.id];
    return { ...country, minPrice: external ?? country.minPrice ?? null };
  };

  const withResortPrice = (resort: Resort): Resort => {
    const external = readPrices().resort?.[resort.id];
    return { ...resort, minPrice: external ?? resort.minPrice ?? null };
  };

  const SEARCH_DEBOUNCE_MS = 300;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  const inputString = ref('');
  const selection = computed<Selection>(() => selectionRef.value);
  const optionsList = ref<OptionGroup[]>([]);
  const controlActivated = ref(false);
  const loading = ref(false);
  const invalid = ref(false);
  const errorText = ref('');
  const countries = ref<Country[]>((options.initialCountries ?? []).map((country) => withCountryPrice(country)));
  const currentCountryResortsSource = ref<Resort[]>([]);
  const currentCountryResorts = ref<Resort[]>([]);

  const selectedCountry = computed<Country | null>(() => getSelectedCountry(selection.value));
  const selectedResorts = computed<Resort[]>(() => getSelectedResorts(selection.value));
  const selectedHotels = computed<Hotel[]>(() => getSelectedHotels(selection.value));

  const firstOptionName = computed<string | null>(() => {
    const first = optionsList.value[0]?.items[0];
    return first ? destinationEntity(first).name : null;
  });

  const label = computed<string>(() => buildLabel(selection.value));

  const inputHint = computed<string>(() =>
    computeInputHint(inputString.value, firstOptionName.value, controlActivated.value),
  );

  const originalLabelChanged = computed<boolean>(() => inputString.value.toLowerCase() !== label.value.toLowerCase());

  const selectAllVisible = computed<boolean>(
    () =>
      controlActivated.value && inputString.value !== '' && (isCountry(selection.value) || isResorts(selection.value)),
  );

  const addMulti = computed<boolean>(
    () => (isResorts(selection.value) || isCountry(selection.value)) && !originalLabelChanged.value,
  );

  const currentInputSelected = computed<boolean>(() => {
    const current = selection.value;
    const input = inputString.value;

    if (input === '') {
      return false;
    }

    let singleItemName: string | null = null;
    if (isCountry(current)) {
      singleItemName = current.country.name;
    } else if (isResorts(current) && current.resorts.length === 1) {
      singleItemName = current.resorts[0].name;
    } else if (isHotels(current) && current.hotels.length === 1) {
      singleItemName = current.hotels[0].name;
    }

    if (singleItemName === null) {
      return false;
    }

    return singleItemName.toLowerCase() === input.toLowerCase();
  });

  const clearVisible = computed<boolean>(
    () => inputString.value !== '' && controlActivated.value && !isNone(selection.value),
  );

  const selectedIds = (): Set<number> =>
    new Set<number>([
      ...selectedResorts.value.map((r) => r.id),
      ...selectedHotels.value.map((h) => h.id),
      ...(selectedCountry.value ? [selectedCountry.value.id] : []),
    ]);

  const rebuildEmptyList = (): void => {
    const items: Destination[] = countries.value.map((c) => ({
      type: 'country',
      country: c,
      resort: null,
      hotel: null,
    }));
    optionsList.value = sortOptions(items, selectedIds(), { pinSelected: true });
  };

  const maybeTrackSelection = (type: DestinationKind, entity: { id: number }): void => {
    if (originalLabelChanged.value && inputString.value !== '') {
      tracker.trackIntelligentSearch({ inputValue: inputString.value, selectedData: { id: entity.id, type } });
    }
  };

  const loadCountryResorts = async (country: Country): Promise<Resort[]> => {
    loading.value = true;
    const source = await getCountryResorts(country, options.resortsUrl);
    currentCountryResortsSource.value = source;
    const resorts = source.map((resort) => withResortPrice(resort));
    currentCountryResorts.value = resorts;
    if (selectedCountry.value?.id === country.id) {
      const items: Destination[] = resorts.map((r) => ({ type: 'resort', country: null, resort: r, hotel: null }));
      optionsList.value = sortOptions(items, selectedIds());
    }
    loading.value = false;
    return resorts;
  };

  const catalogFor = (country: Country): Resort[] => countryResortCatalog(country, currentCountryResorts.value);
  const ensureCountryCatalog = async (country: Country): Promise<void> => {
    const catalog = await loadCountryResorts(country);
    const current = selection.value;
    if (isNone(current) || current.country.id !== country.id) {
      return;
    }
    const existing = current.country.resorts;
    const needsCatalog = existing === undefined || (existing.length === 0 && catalog.length > 0);
    if (!needsCatalog) {
      return;
    }
    setSelection({ ...current, country: { ...current.country, resorts: catalog } });
  };

  const rebuildCountryResorts = (country: Country): void => {
    const cached = currentCountryResorts.value;
    if (cached.length > 0 && cached[0].countryId === country.id) {
      const items: Destination[] = cached.map((r) => ({ type: 'resort', country: null, resort: r, hotel: null }));
      optionsList.value = sortOptions(items, selectedIds());
    } else {
      loadCountryResorts(country);
    }
  };

  const syncInputToLabel = (): void => {
    inputString.value = label.value;
  };

  const resolveCountry = (countryId: number, fallbackName: string): Country =>
    resolveCountryFromList(countryId, fallbackName, selectedCountry.value, countries.value);

  const selectCountry = async (country: Country): Promise<void> => {
    maybeTrackSelection('country', country);
    setSelection(selectCountryTransition(country));
    syncInputToLabel();
    await ensureCountryCatalog(country);
  };

  const toggleResort = async (resort: Resort, fromAutoSelect = false): Promise<void> => {
    maybeTrackSelection('resort', resort);
    const country: Country = resolveCountry(resort.countryId, resort.countryName);

    const previousCountryId = selectedCountry.value?.id ?? null;
    setSelection(toggleResortTransition(selection.value, resort, country, catalogFor(country)));
    if (!(fromAutoSelect && controlActivated.value)) {
      syncInputToLabel();
    }

    if (previousCountryId === resort.countryId) {
      const items = optionsList.value.flatMap((g) => g.items);
      optionsList.value = sortOptions(items, selectedIds());
    } else {
      await ensureCountryCatalog(country);
    }
  };

  const toggleSelectAll = (): void => {
    if (isCountry(selection.value)) {
      inputString.value = '';
      setSelection(emptySelection());
      rebuildEmptyList();
      return;
    }
    if (selectedCountry.value) {
      setSelection(selectAllResortsTransition(selectedCountry.value, catalogFor(selectedCountry.value)));
      syncInputToLabel();
    }
  };

  const selectHotel = async (hotel: Hotel): Promise<void> => {
    maybeTrackSelection('hotel', hotel);
    const country: Country = resolveCountry(hotel.countryId, hotel.countryName);
    setSelection(selectHotelTransition(selection.value, hotel, country));
    syncInputToLabel();
    controlActivated.value = false;
    await ensureCountryCatalog(country);
  };

  const selectDestination = (destination: Destination, fromAutoSelect = false): void => {
    if (destination.type === 'country') {
      selectCountry(destination.country);
    } else if (destination.type === 'resort') {
      toggleResort(destination.resort, fromAutoSelect);
    } else {
      selectHotel(destination.hotel);
    }
  };

  const open = (): void => {
    controlActivated.value = true;
    if (inputString.value === '') {
      rebuildEmptyList();
      return;
    }
    const current = selection.value;
    if ((isCountry(current) || isResorts(current)) && !originalLabelChanged.value) {
      rebuildCountryResorts(current.country);
    }
  };

  const close = (): void => {
    controlActivated.value = false;
    errorText.value = '';
    invalid.value = false;
    if (isNone(selection.value) && !isNone(lastNonEmptySelection)) {
      setSelection(lastNonEmptySelection);
    }
    if (!isNone(selection.value)) {
      inputString.value = label.value;
    }
  };

  const runSearch = async (v: string): Promise<void> => {
    const results = await getOptions(v, countries.value, options.searchUrl);
    optionsList.value = groupOptions(results);
    invalid.value = results.length === 0;

    const first = results[0];
    if (first && destinationEntity(first).name.toLowerCase() === v.toLowerCase()) {
      selectDestination(first, true);
    }
  };

  const scheduleSearch = (v: string): Promise<void> =>
    new Promise<void>((resolve) => {
      if (searchTimer !== null) {
        clearTimeout(searchTimer);
      }
      searchTimer = setTimeout(() => {
        searchTimer = null;
        return runSearch(v).then(resolve);
      }, SEARCH_DEBOUNCE_MS);
    });

  const setInput = async (v: string): Promise<void> => {
    inputString.value = v;

    if (v === '') {
      if (searchTimer !== null) {
        clearTimeout(searchTimer);
        searchTimer = null;
      }
      setSelection(emptySelection());
      rebuildEmptyList();
      invalid.value = false;
      return;
    }

    if (originalLabelChanged.value) {
      await scheduleSearch(v);
      return;
    }

    rebuildEmptyList();
  };

  const acceptHint = (): void => {
    const first = optionsList.value[0]?.items[0];
    if (inputHint.value !== '' && first) {
      selectDestination(first);
    }
  };

  const clear = (): void => {
    inputString.value = '';
    setSelection(emptySelection());
    lastNonEmptySelection = emptySelection();
    rebuildEmptyList();
    errorText.value = '';
    invalid.value = false;
  };

  const clearInput = (): void => {
    inputString.value = '';
    setSelection(emptySelection());
    rebuildEmptyList();
    errorText.value = '';
    invalid.value = false;
  };

  const validate = (): boolean => {
    if (isNone(selection.value)) {
      invalid.value = true;
      errorText.value = inputString.value === '' ? DESTINATION_TEXTS.errorEmpty : DESTINATION_TEXTS.errorInvalid;
      return false;
    }
    errorText.value = '';
    return true;
  };

  const applyExternalSelection = async (sel: Selection): Promise<void> => {
    inputString.value = buildLabel(sel);
    controlActivated.value = false;
    const country = getSelectedCountry(sel);
    if (country) {
      await ensureCountryCatalog(country);
    }
  };

  watch(
    model,
    (value) => {
      if (!value || isEqual(value, lastEmitted)) {
        return;
      }
      selectionRef.value = value;
      lastNonEmptySelection = isNone(value) ? emptySelection() : value;
      if (!isNone(value)) {
        errorText.value = '';
        invalid.value = false;
      }
      if (!controlActivated.value) {
        applyExternalSelection(value);
      }
    },
    { immediate: true },
  );

  const setCountries = (list: Country[]): void => {
    countries.value = list.map((country) => withCountryPrice(country));
    if (controlActivated.value && inputString.value === '') {
      rebuildEmptyList();
    }
  };
  const repriceResorts = (): void => {
    if (currentCountryResortsSource.value.length === 0) {
      return;
    }
    const repriced = currentCountryResortsSource.value.map((resort) => withResortPrice(resort));
    currentCountryResorts.value = repriced;

    const byId = new Map<number, Resort>(repriced.map((r) => [r.id, r]));
    let changed = false;
    const next = optionsList.value.map((group) => {
      if (group.kind !== 'resort') {
        return group;
      }
      const items = group.items.map((item): Destination => {
        if (item.type !== 'resort') {
          return item;
        }
        const fresh = byId.get(item.resort.id);
        if (fresh && fresh.minPrice !== item.resort.minPrice) {
          changed = true;
          return { type: 'resort', country: null, resort: fresh, hotel: null };
        }
        return item;
      });
      return { ...group, items };
    });

    if (changed) {
      optionsList.value = next;
    }
  };

  return {
    inputString,
    selection,
    optionsList,
    controlActivated,
    loading,
    invalid,
    errorText,
    selectedCountry,
    selectedResorts,
    selectedHotels,
    firstOptionName,
    label,
    inputHint,
    originalLabelChanged,
    selectAllVisible,
    addMulti,
    currentInputSelected,
    clearVisible,
    selectDestination,
    selectCountry,
    toggleResort,
    toggleSelectAll,
    selectHotel,
    open,
    close,
    setInput,
    acceptHint,
    clear,
    clearInput,
    validate,
    setCountries,
    repriceResorts,
  };
}
